import { createEntityAdapter } from "@reduxjs/toolkit";

import apiSlice from "../api/apiSlice";
import { effectiveAtQuery, withBusinessValidity } from "../../utils/businessValidity";

const membersAdapter = createEntityAdapter({});

const initialState = membersAdapter.getInitialState({});

export const membersApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* GET */
    getMembersByCommunityId: builder.query({
      query: ({ communityId, excludeDeleted }) => ({
        url:
          `/communities/${communityId}/members?` +
          `${excludeDeleted !== undefined ? `&exclude_deleted=${excludeDeleted}` : ""}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      transformResponse: (responseData) => {
        if (responseData) {
          return membersAdapter.setAll(initialState, responseData);
        }
        return initialState;
      },
      providesTags: (result) => {
        if (result?.ids) {
          return [
            { type: "Member", id: "LIST" },
            ...result.ids.map((id) => ({ type: "Member", id })),
          ];
        }
        return [{ type: "Member", id: "LIST" }];
      },
    }),
    /* POST */
    addNewMember: builder.mutation({
      query: ({ communityId, member }) => ({
        url: `/communities/${communityId}/users`,
        method: "POST",
        body: {
          ...withBusinessValidity(member),
        },
      }),
      invalidatesTags: [{ type: "Member", id: "LIST" }],
    }),
    /* UPDATE */
    updateMemberById: builder.mutation({
      query: ({ communityId, memberId, data }) => ({
        url: `/communities/${communityId}/users/${memberId}`,
        method: "PUT",
        body: {
          ...withBusinessValidity(data),
        },
      }),
      invalidatesTags: (_, __, arg) => [
        { type: "Member", id: arg.memberId },
        { type: "BusinessValidity", id: `membership:${arg.memberId}` },
      ],
    }),
    /* DELETE */
    deleteMemberById: builder.mutation({
      query: ({ communityId, memberId, effectiveAt }) => ({
        url: `/communities/${communityId}/users/${memberId}?${effectiveAtQuery(effectiveAt)}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, arg) => [
        { type: "Member", id: arg.id },
        { type: "Resource", id: "LIST" },
      ],
    }),
    /* INVITE */
    inviteMember: builder.mutation({
      query: ({ communityId, memberId }) => ({
        url: `/communities/${communityId}/users/${memberId}/invite`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "Member", id: "LIST" }],
    }),
  }),
});

export const {
  useAddNewMemberMutation,
  useGetMembersByCommunityIdQuery,
  useUpdateMemberByIdMutation,
  useDeleteMemberByIdMutation,
  useInviteMemberMutation,
} = membersApiSlice;

export const { selectAll: selectAllMembers } = membersAdapter.getSelectors();
